import { zonedNow } from '../lib/dates';
import { useEffect, useMemo, useState } from 'react';
import { TERMS, TERM_LABELS, toDateKey, type Assessment, type Term } from '@td/shared';
import {
  useAssessments,
  useDeleteAssessment,
  useSaveScores,
  useScores,
} from '../hooks/useAssessments';
import { useStudents } from '../hooks/useStudents';
import { ScoreGrid } from '../components/ScoreGrid';
import { GrowthView } from '../components/GrowthView';
import { AssessmentEditor } from '../components/AssessmentEditor';
import { AddAssessmentMenu } from '../components/AddAssessmentMenu';
import { ClassCharts } from '../components/ClassCharts';
import { useConfirm } from '../components/ConfirmDialog';
import { usePrivacy } from '../privacy/PrivacyContext';

const VIEWS = ['enter', 'growth', 'charts'] as const;
type DataView = (typeof VIEWS)[number];

const VIEW_LABELS: Record<DataView, string> = {
  enter: 'Enter scores',
  growth: 'Growth',
  charts: 'Class charts',
};

export function DataPage() {
  const { data: assessments = [], isLoading } = useAssessments();
  const { data: students = [] } = useStudents();
  const removeAssessment = useDeleteAssessment();
  const confirm = useConfirm();
  const { privacy, toggle } = usePrivacy();

  // Null means the overview. Opening an assessment is a place you can leave
  // again, rather than a state the page drops into and never comes out of.
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [view, setView] = useState<DataView>('enter');
  const [term, setTerm] = useState<Term>('BOY');
  const [date, setDate] = useState(toDateKey(zonedNow()));
  const [measureId, setMeasureId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Assessment | null | undefined>(undefined);
  const [adding, setAdding] = useState(false);

  const assessment = assessments.find((a) => a.id === assessmentId) ?? null;
  const { data: scores = [] } = useScores(assessment?.id ?? null);
  const save = useSaveScores(assessment?.id ?? '');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const roster = useMemo(() => students.filter((s) => s.active), [students]);

  // Keep the measure and student selections pointing at something real as the
  // assessment or roster changes underneath them.
  useEffect(() => {
    if (assessment && !assessment.measures.some((m) => m.id === measureId)) {
      setMeasureId(assessment.measures[0]?.id ?? null);
    }
  }, [assessment, measureId]);

  const measure = assessment?.measures.find((m) => m.id === measureId) ?? null;

  if (isLoading) return <p className="text-sm text-stone">Loading…</p>;

  return (
    <div className="mx-auto max-w-6xl">
      <header className="page-header flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div className="min-w-0">
          {assessment ? (
            <>
              <h1 className="truncate text-2xl">{assessment.name}</h1>
              <p className="mt-0.5 text-sm text-stone">
                {assessment.subject ? `${assessment.subject} · ` : ''}
                {assessment.measures.length} measure
                {assessment.measures.length === 1 ? '' : 's'} · {roster.length} students
              </p>
            </>
          ) : (
            <h1 className="text-2xl">Data</h1>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {assessment && view === 'enter' && (
            <>
              <span className="text-xs text-stone" aria-live="polite">
                {save.isPending
                  ? 'Saving…'
                  : save.isError
                    ? 'Not saved'
                    : savedAt
                      ? `Saved ${savedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
                      : 'Saves as you type'}
              </span>
              <button
                onClick={() => (document.activeElement as HTMLElement | null)?.blur()}
                className="btn-secondary"
                title="Commit the cell you are in — everything before it is already saved"
              >
                Save
              </button>
            </>
          )}
          <button onClick={toggle} aria-pressed={privacy} className="btn-secondary">
            {privacy ? 'Privacy on' : 'Privacy'}
          </button>
          <button onClick={() => window.print()} className="btn-secondary">
            Print
          </button>
          <button onClick={() => setAdding(true)} className="btn-primary">
            Add assessment
          </button>
        </div>
      </header>

      {assessments.length === 0 ? (
        <section className="card max-w-lg">
          <h2 className="mb-1 text-base">Start with what you already give</h2>
          <p className="mb-4 text-sm leading-relaxed text-walnut">
            NWEA MAP, Acadience, CKLA and a blank district one are ready to go, with their real
            measures and units. You can add as many as you like, and change any of them later.
          </p>
          <button onClick={() => setAdding(true)} className="btn-primary">
            Choose an assessment
          </button>
        </section>
      ) : !assessment ? (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {assessments.map((a) => (
            <li key={a.id}>
              <button
                onClick={() => setAssessmentId(a.id)}
                className="flex h-full w-full flex-col rounded-card border border-sand bg-white p-4 text-left transition-colors hover:border-clay hover:bg-linen/40"
              >
                <span className="text-sm text-espresso">{a.name}</span>
                <span className="mt-0.5 text-[11px] text-stone">
                  {a.subject ? `${a.subject} · ` : ''}
                  {a.termly ? 'BOY / MOY / EOY' : 'By date'}
                </span>
                <span className="mt-3 flex flex-wrap gap-1">
                  {a.measures.map((m) => (
                    <span
                      key={m.id}
                      className="rounded bg-linen px-1.5 py-0.5 text-[10px] text-walnut"
                    >
                      {m.name}
                    </span>
                  ))}
                </span>
              </button>
            </li>
          ))}

          <li>
            <button
              onClick={() => setAdding(true)}
              className="flex h-full w-full items-center justify-center rounded-card border border-dashed border-sand p-4 text-sm text-terracotta transition-colors hover:border-clay hover:bg-linen/40"
            >
              + Add another assessment
            </button>
          </li>
        </ul>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
            <div className="flex items-center gap-1" role="group" aria-label="View">
              {VIEWS.map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  aria-pressed={view === v}
                  className={[
                    'rounded-lg px-2.5 py-1 text-xs transition-colors',
                    view === v ? 'accent-fill' : 'bg-linen text-walnut hover:bg-sand/60',
                  ].join(' ')}
                >
                  {VIEW_LABELS[v]}
                </button>
              ))}
            </div>

            {assessment && (
              <>
                <button onClick={() => setEditing(assessment)} className="btn-soft">
                  Edit measures
                </button>
                <button
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Delete "${assessment.name}"?`,
                      body: 'Every score recorded against it goes too, and none of it can be recovered.',
                      confirmLabel: 'Delete',
                      danger: true,
                    });
                    if (ok) {
                      removeAssessment.mutate(assessment.id);
                      setAssessmentId(null);
                    }
                  }}
                  className="btn-soft-danger"
                >
                  Delete
                </button>
              </>
            )}
          </div>

          {assessment && (
            <div className="mb-3 hidden print:block">
              <h1 className="text-xl">
                {assessment.name}
                {view === 'enter' && assessment.termly ? ` · ${term}` : ''}
              </h1>
            </div>
          )}

          {!assessment ? null : view === 'enter' ? (
            <section className="card">
              <div className="mb-3 flex flex-wrap items-center gap-2 print:hidden">
                {assessment.termly ? (
                  <div className="flex items-center gap-1" role="group" aria-label="Term">
                    {TERMS.map((t) => (
                      <button
                        key={t}
                        onClick={() => setTerm(t)}
                        aria-pressed={term === t}
                        title={TERM_LABELS[t]}
                        className={[
                          'rounded-lg px-2.5 py-1 text-xs transition-colors',
                          term === t
                            ? 'bg-terracotta text-cream'
                            : 'bg-linen text-walnut hover:bg-sand/60',
                        ].join(' ')}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                ) : (
                  <label className="flex items-center gap-2 text-xs text-walnut">
                    Date given
                    <input
                      className="field w-auto py-1.5"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </label>
                )}
              </div>

              <ScoreGrid
                assessment={assessment}
                students={roster}
                scores={scores}
                term={assessment.termly ? term : null}
                date={assessment.termly ? '' : date}
                save={save}
                onSaved={() => setSavedAt(new Date())}
              />
            </section>
          ) : view === 'growth' ? (
            <section className="card">
              <div className="mb-3 flex flex-wrap gap-1 print:hidden">
                {assessment.measures.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setMeasureId(m.id)}
                    aria-pressed={measureId === m.id}
                    className={[
                      'rounded-lg px-2.5 py-1 text-xs transition-colors',
                      measureId === m.id
                        ? 'bg-terracotta text-cream'
                        : 'bg-linen text-walnut hover:bg-sand/60',
                    ].join(' ')}
                  >
                    {m.name}
                  </button>
                ))}
              </div>

              {measure ? (
                <GrowthView
                  assessment={assessment}
                  measure={measure}
                  students={roster}
                  scores={scores}
                />
              ) : (
                <p className="text-sm text-stone">This assessment has no measures yet.</p>
              )}
            </section>
          ) : (
            <section className="card">
              <div className="mb-3 flex flex-wrap items-center gap-2 print:hidden">
                <div className="flex flex-wrap gap-1">
                  {assessment.measures.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setMeasureId(m.id)}
                      aria-pressed={measureId === m.id}
                      className={[
                        'rounded-lg px-2.5 py-1 text-xs transition-colors',
                        measureId === m.id
                          ? 'bg-terracotta text-cream'
                          : 'bg-linen text-walnut hover:bg-sand/60',
                      ].join(' ')}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-1" role="group" aria-label="Term">
                  {TERMS.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTerm(t)}
                      aria-pressed={term === t}
                      title={TERM_LABELS[t]}
                      className={[
                        'rounded-lg px-2 py-1 text-xs transition-colors',
                        term === t ? 'accent-fill' : 'bg-linen text-walnut hover:bg-sand/60',
                      ].join(' ')}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {measure ? (
                <ClassCharts
                  assessment={assessment}
                  measure={measure}
                  students={roster}
                  scores={scores}
                  term={term}
                />
              ) : (
                <p className="text-sm text-stone">This assessment has no measures yet.</p>
              )}
            </section>
          )}

          {/*
            The way out sits under the work rather than over the title. You go
            back when you have finished with what is on screen, and that is
            where your eye already is — and it is a terracotta button like
            every other action on the page, not a quiet outline.
          */}
          <div className="mt-4 print:hidden">
            <button onClick={() => setAssessmentId(null)} className="btn-primary">
              ← All assessments
            </button>
          </div>
        </>
      )}

      {adding && (
        <AddAssessmentMenu
          existingNames={assessments.map((a) => a.name)}
          onBlank={() => {
            setAdding(false);
            setEditing(null);
          }}
          onClose={() => setAdding(false)}
        />
      )}

      {editing !== undefined && (
        <AssessmentEditor assessment={editing} onClose={() => setEditing(undefined)} />
      )}
    </div>
  );
}
